import {ChangeDetectorRef, Component, ElementRef, Inject, NgZone, OnDestroy, OnInit} from '@angular/core';
import {from, Observable, of, Subscription} from 'rxjs';
import {catchError, concatMap, mergeMap, switchMap, take} from 'rxjs/operators';
import {cardPaymentMethodPayload, Dropin} from 'braintree-web-drop-in';
import {BusyService} from '@unleashed/services/common';
import {APP_OPTIONS, AppOptions, BRAND_OPTIONS, BrandOptions} from '@unleashed/common/config';
import {BookingApiService, InvoiceApiService, PaymentsApiService} from '@unleashed/api/booking';
import {AccountSourceType, User} from '@unleashed/models/account';
import {AuthorizationService} from '@unleashed/services/account';
import {Booking, NavigationSteps} from '@unleashed/models/booking';
import {BookingSessionService} from '@unleashed/services/booking';
import {Park} from '@unleashed/models/park';
import {BookingInvoiceApiService} from '@unleashed/api/booking/booking-invoice-api.service';
import {bookingDepositIdempotencyKey} from '../../utils/idempotency';
import {Payment} from '@unleashed/models/booking/payment';
import {VaultedCardApiService} from '@unleashed/api/account';
import {PaymentMethod} from '@unleashed/models/account/PaymentMethod';
import {digitalWalletPrefix} from '@unleashed/common/utils';
import {BillingData} from '@unleashed/models/account/BillingData';
import {PaymentOrigins} from '@unleashed/models/booking/paymentOrigins';
import {JustifiApiService} from '@unleashed/api/justifi/justifi-api.service';
import {GoogleTagManagerService} from 'angular-google-tag-manager';
import {ActivatedRoute} from '@angular/router';
import {CheckoutTokenRequest} from '@unleashed/models/booking/checkoutTokenRequest';
import {CheckoutToken} from '@unleashed/models/booking/checkoutToken';

@Component({
  selector: 'ua-wizard-checkout-step',
  templateUrl: './wizard-checkout-step.component.html',
  styleUrls: ['./wizard-checkout-step.component.scss']
})
export class WizardCheckoutStepComponent implements OnInit, OnDestroy {
  booking?: Booking;
  dropinInstance?: Dropin;
  paymentInitialized = false;
  user?: User;
  accessToken = '';
  documentsAcknowledged = false;
  depositAcknowledged = false;
  weatherAcknowledged = false;
  eSignatureLink: string;
  privacyLink: string;
  termsLink: string;
  uaAdmin: string;
  braintreeError?: string;
  showSpecialEventText: boolean;
  brandName: string;
  brandId: number;
  park?: Park | null;
  paymentProcessorVersion?: number;
  platformSubAccountId?: string;
  platformCanadaSubAccountId?: string;
  justifiElem: ElementRef = new ElementRef(null);
  isAvs: boolean; // Avs: address verification
  justifiError = false;
  justifiErrorMsg?: string;
  justifiAuthToken = '';
  justifiCheckoutId = '';
  parkId?: string;
  useDefaultCard = true;
  addNewCard = false;
  defaultCard?: PaymentMethod;
  defaultCardValue?: string;
  filteredCards?: PaymentMethod[] = [];
  currencyCode?: string;
  country?: string;
  subscriptions = new Subscription();

  hideSteps?: boolean;
  isSpecialEvent?: boolean;
  // STORE-203: true from click on Pay until success/error completes. Drives [disabled]
  // and the re-entry guards so a same-tab double-click can't double-submit the deposit.
  paying = false;
  // STORE-203: separate flag for preCompleteHook re-entry. The card-form path enters
  // preCompleteHook with paying=true (set by payNow), so paying alone can't distinguish
  // a legitimate first entry from a wallet double-tap. This flag flips true on entry
  // and is reset on error/success so a retry isn't blocked.
  walletProcessing = false;

  constructor(
    private sessionService: BookingSessionService,
    private authService: AuthorizationService,
    private paymentsApi: PaymentsApiService,
    private busyService: BusyService,
    private bookingApi: BookingApiService,
    private invoiceApi: InvoiceApiService,
    private bookingInvoiceApi: BookingInvoiceApiService,
    private vaultedCardApi: VaultedCardApiService,
    private justifiApiService: JustifiApiService,
    private gtmService: GoogleTagManagerService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    @Inject(APP_OPTIONS) appOptions: AppOptions,
    @Inject(BRAND_OPTIONS) private brandOptions: BrandOptions
  )
  {
    this.uaAdmin = appOptions.uaAdminUrl;
    this.eSignatureLink = brandOptions.eSignatureAgreementLink;
    this.privacyLink = brandOptions.privacyLink;
    this.termsLink = brandOptions.termsLink;
    this.showSpecialEventText = brandOptions.showSpecialEventText;
    this.brandName = brandOptions.name;
    this.brandId = brandOptions.brandId;
    this.isAvs = brandOptions.isAvs;
  }

  ngOnInit(): void {
    const hideSteps = this.route.snapshot.queryParamMap.get('hideSteps') as string | undefined;
    this.hideSteps = hideSteps === 'true';
    this.cdr.detectChanges();

    this.busyService.set('vaultedCards', true);

    this.subscriptions.add(
      this.sessionService.getConfig().pipe(
        switchMap(config => {
          this.platformSubAccountId = config.justifiSubAccountId;
          this.platformCanadaSubAccountId = config.justifiCanadaSubAccountId;
          return this.sessionService.getSession();
        }),
        // only need initial session; later updates (setBookingCookie, removeCart, navigateToStep) should not re-trigger vault calls
        take(1),
        mergeMap(session => {
          this.paymentProcessorVersion = (this.platformSubAccountId || this.platformCanadaSubAccountId) ?
            session?.park?.paymentProcessorVersion : 1;
          return of(session);
        })
      ).subscribe(session => {
        this.park = session?.park;
        this.parkId = session?.park?.id;
        this.currencyCode = session.park?.currencyCode;
        this.country = session.park?.address?.country?.toLowerCase().trim() ?? 'us';

        this.vaultedCardApi.getVaultedCards(this.paymentProcessorVersion, this.country)
          .subscribe(cards =>   {
            this.defaultCard = cards?.find(c => c.isDefault);
            this.filteredCards = cards?.filter(cc => !cc.isExpired);

            // Use local variables to avoid triggering unnecessary UI change for JustiFi component
            let useDefaultCard = this.useDefaultCard;
            let addNewCard = false;

            if (this.defaultCard?.isExpired) {
              // [STORE-491] An expired default card is not usable. Previously only the
              // "default expired AND every card expired" case was handled here; an expired
              // default with another valid card on file fell through to the "valid default"
              // branch below, left useDefaultCard=true, and trapped the user on the greyed
              // expired card (the toggle that clears useDefaultCard is hidden for an expired
              // default, so no dropdown / Add Payment Method ever rendered). Treat any expired
              // default like no usable default: show the selector when valid cards remain,
              // otherwise go straight to adding a new card.
              useDefaultCard = false;
              if (this.filteredCards && this.filteredCards.length > 0) {
                this.paymentInitialized = true;
                this.defaultCardValue = this.filteredCards[0].id; // Pre-select first valid card
              }
              else {
                addNewCard = true;
              }
            }
            else if (!this.defaultCard) {
              // no default card exists
              useDefaultCard = false;
              addNewCard = !this.filteredCards || this.filteredCards.length === 0;

              // If we have vaulted cards to select from, payment is initialized
              if (this.filteredCards && this.filteredCards.length > 0) {
                this.paymentInitialized = true;
                this.defaultCardValue = this.filteredCards[0].id; // Pre-select first card
              }
            }
            else if (this.defaultCard && useDefaultCard) {
              // default card exists and is valid
              this.paymentInitialized = true;
              this.defaultCardValue = this.defaultCard?.id;
            }

            if (this.paymentProcessorVersion === 2 && !useDefaultCard && addNewCard) {
              from(this.addPaymentMethodClick()).subscribe();
            }
            else {
              this.useDefaultCard = useDefaultCard;
              this.addNewCard = addNewCard;
            }

            this.busyService.set('vaultedCards', false);
          });
      })
    );

    this.subscriptions.add(this.sessionService.getBooking()
      .pipe(
        take(1),
        concatMap(booking => {
          if (!booking.cookieId) {
            this.sessionService.navigateToStep(NavigationSteps.Confirmation);
          }
          this.booking = booking;
          this.isSpecialEvent = booking.isSpecialEvent;
          this.cdr.detectChanges();
          return this.authService.getUser();
        }),
        concatMap(user => {
          this.user = user;
          if (!user.loggedIn) {
            this.sessionService.navigateToStep(NavigationSteps.Review);
          }
          else if (user.accountSourceId === AccountSourceType.ThriveScoreProvisional) {
            this.sessionService.navigateToStep(NavigationSteps.Review);
          }
          else if (!user.address?.zipCode && !user.impersonated) {
            this.sessionService.navigateToStep(NavigationSteps.Review);
          }

          return this.authService.getAccessToken();
        }))
      .subscribe(accessToken => this.accessToken = accessToken));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.busyService.clear();
  }

  back(): void {
    if (!this.booking) {
      return;
    }

    this.booking.step = 6;
    this.sessionService.navigateToStep(NavigationSteps.Review);
  }

  handleError = (error?: any) => {
    let errorMsg: string;

    // Extract error message from different error formats
    if (typeof error === 'string') {
      errorMsg = error;
    } else if (typeof error?.error === 'string' && error.error.trim() && !error.error.trim().startsWith('<')) {
      // Card-decline responses (e.g. the AVS postal-code decline) come back as a 400 whose body is a
      // bare string; surface that friendly message to the guest instead of the generic fallback.
      errorMsg = error.error.trim();
    } else if (error?.error?.message) {
      errorMsg = error.error.message;
    } else if (error?.message) {
      errorMsg = error.message;
    } else if (error?.statusText) {
      errorMsg = `Error: ${error.statusText} (${error.status})`;
    } else {
      errorMsg = 'An unexpected error occurred.';
    }

    this.justifiError = true;
    this.justifiErrorMsg = errorMsg;
    this.busyService.set('pay', false);
    this.paying = false;
    this.walletProcessing = false;
    // [CORE-547] Do NOT clear the idempotency key here. Clearing it meant a retry after a false
    // failure (charge captured server-side, client saw an error) presented a NEW key and re-charged.
    // The backend now handles both retry flavors on a stable key: declined attempts get a derived
    // per-attempt processor key (CORE-524), and already-captured attempts collapse/resume (CORE-547).
  }

  async payNow(): Promise<void> {
    // Re-entry guard. [disabled] handles the common case but won't catch wallet-payment
    // callbacks that fire outside Angular's zone before change detection updates.
    if (this.paying) {
      return;
    }
    this.paying = true;

    // [CORE-547] Stable per-cart key derived from the cart cookie (was mint-once + clear-on-error,
    // which re-charged on a retry after a false failure and did not survive a page refresh).
    if (this.booking?.cookieId) {
      this.booking.idempotencyKey = bookingDepositIdempotencyKey(this.booking.cookieId);
    }

    this.gtmService.pushTag({
      event: 'checkoutButtonClicked'
    });

    if (this.paymentProcessorVersion === 1) {
      this.payBrainTree();
    }
    else {
      await this.payJustifi();
    }
  }

  async payJustifi(): Promise<void> {
    this.justifiError = false;
    this.busyService.set('pay', true);

    // JustiFi billing form components handle address fields; we only need to pass email
    const billingData: BillingData = {
      email: `${this.user?.accountId ?? 'guest'}@commandcenter.unleashedbrands.com`,
    };

    if (!this.addNewCard) {
      this.payJustifiHandler();
      return;
    }

    // handle new card payment below
    // Validate the card form subcomponent before submitting
    const validation = await this.justifiElem.nativeElement.querySelector('justifi-card-form').validate();

    if (!validation.isValid) {
      this.busyService.set('pay', false);
      this.paying = false;
      return;
    }

    // This just returns a Promise<void> and you should listen for submit-event to handle completion.
    // will do this.payJustifiHandler() in its event handler
    try {
      await this.justifiElem.nativeElement.submitCheckout(billingData);
    }
    catch (error) {
      this.handleError(error);
    }
  }

  // (1) Party Store only takes deposits today. Final payments are done at POS.
  // (2) Party-store's only payment flow is booking deposits — prepaid block applies unconditionally.
  //      If other payment types are added in the future, this may need conditional logic (like command-center's blockPrepaidCard prop).
  // (3) Prepaid cards are blocked for booking deposits (party-store) and membership purchases (command-center).
  //      Party-store does not have a membership purchase flow, so only "booking deposit" in the error msg here.
  checkPrepaidAndComplete(paymentToken: string): void {
    const countryId = this.country === 'ca' ? 'CA' : 'US';  // country is already lower case

    this.justifiApiService.getPaymentMethod(paymentToken, countryId).subscribe({
      next: (result) => {
        if (result?.card?.binDetails?.fundingSource?.toLowerCase() === 'prepaid') {
          this.handleError('Prepaid cards cannot be used for booking deposits.');
          return;
        }

        this.payJustifiHandler(paymentToken);
      },
      error: () => {
        // Fail-open: don't block a legitimate payment just because the prepaid-check API is unreachable.
        // Worst case a prepaid card slips through; blocking all payments during an outage is worse.
        this.payJustifiHandler(paymentToken);
      }
    });
  }

  payJustifiHandler(tokenId?: string): void {
    if (this.addNewCard && !tokenId) {
      const errMsg = 'Error in getting JustiFi payment method token.';
      this.handleError(errMsg);
      throw new Error(errMsg);
    }

    // Wallet payments (Apple/Google Pay) bypass payNow() — derive here as a fallback so those
    // paths still get the same stable per-cart key. [CORE-547]
    if (this.booking?.cookieId && !this.booking.idempotencyKey) {
      this.booking.idempotencyKey = bookingDepositIdempotencyKey(this.booking.cookieId);
    }

    const payload: Payment = {
      idempotencyKey: this.booking?.idempotencyKey,
      token: this.addNewCard ? tokenId : this.defaultCardValue
    };

    const paymentOriginId = !this.user?.impersonated ? PaymentOrigins.Ecommerce : PaymentOrigins.Admin;

    let processDeposit$: Observable<string>;

    if (!this.booking?.isSpecialEvent) {
      processDeposit$ = this.bookingInvoiceApi.processCartDeposit(
        paymentOriginId, this.brandId, this.accessToken, payload, this.parkId, this.booking?.cookieId);
    }
    else {
      processDeposit$ = this.bookingInvoiceApi.processSpecialEventDeposit(
        paymentOriginId, this.brandId, this.accessToken, payload, this.parkId, this.booking?.cookieId);
    }

    if (this.redirectToAdminAfterPayment) {
      processDeposit$
        .pipe(
          concatMap(confirmation => this.bookingInvoiceApi.getInvoice(confirmation, this.accessToken, this.brandId, this.parkId)),
          concatMap(invoice => this.postJustifiHandler().pipe(
            switchMap(() => {
              this.authService.logout();

              if (invoice.isSpecialEvent) {
                document.location.href = `${this.uaAdmin}/parties/special-events/${invoice.bookingId}`;
              }
              else {
                document.location.href = `${this.uaAdmin}/parties/booking/${invoice.bookingId}?parkId=${invoice.park.id}`;
              }

              return of(undefined);
            })
          ))
        )
        .subscribe({ error: this.handleError });
    }
    else {
      processDeposit$
        .pipe(
          concatMap(r => {
            this.sessionService.setBookingCookie(r);
            this.sessionService.removeCart(true);
            return this.postJustifiHandler();
          })
        )
        .subscribe({
          // Use Angular router (not window.location.href) to avoid full page reload.
          // On iPhone, window.location.href causes the app to re-bootstrap, losing the booking
          // session/cookie and redirecting to the start page instead of confirmation.
          next: () => {
            this.paying = false;
            this.walletProcessing = false;
            this.sessionService.navigateToStep(NavigationSteps.Confirmation);
          },
          error: this.handleError
        });
    }
  }

  postJustifiHandler(): Observable<boolean | void> {
    if (this.booking?.cookieId) {
      this.bookingApi.deleteCart(this.booking.cookieId);
    }

    this.busyService.set('pay', false);

    if (this.addNewCard && this.justifiCheckoutId) {
      // Cleanup only — payment already succeeded. Swallow errors so navigation isn't blocked.
      return this.justifiApiService.deleteCheckout(this.justifiCheckoutId).pipe(
        catchError(() => of(undefined))
      );
    }

    return of(undefined);
  }

  payBrainTree(): void {
    this.busyService.set('pay', true);
    this.dropinInstance?.requestPaymentMethod({
      threeDSecure: {
        amount: this.booking?.depositAmount?.toString() ?? '',
        mobilePhoneNumber: this.user?.phoneNumber,
        email: this.user?.emailAddress,
        billingAddress: {
          givenName: this.user?.firstName,
          surname: this.user?.lastName,
          streetAddress: this.user?.address?.streetAddress,
          locality: this.user?.address?.city,
          region: this.user?.address?.state,
          postalCode: this.user?.address?.zipCode,
          countryCodeAlpha2: this.user?.address?.country.trim()
        }
      }
    }, (error, payload) => {
      if (error) {
        this.busyService.set('pay', false);
        this.paying = false;
        if (this.booking) {
          this.booking.idempotencyKey = undefined;
        }
        // Surface the failure in our own banner instead of throwing — a thrown error
        // here propagates as an unhandled exception and the user is left with no
        // feedback about why Pay did nothing. @types/braintree-web-drop-in types
        // `error` as plain `object`, but Braintree errors carry a `message` field.
        this.braintreeError = (error as { message?: string })?.message ?? 'Payment failed. Please try again.';
        return;
      }
      if (!this.booking) {
        this.paying = false;
        return;
      }

      const card = payload as cardPaymentMethodPayload;
      if (!card) {
        // unsupported payment method?
        this.paying = false;
        if (this.booking) {
          this.booking.idempotencyKey = undefined;
        }
        return;
      }

      let processDeposit$: Observable<string>;

      if (this.booking.isSpecialEvent) {
        processDeposit$ = this.paymentsApi.processBookingDeposit(this.booking.cookieId ?? '', card, this.accessToken);
      } else {
        processDeposit$ = this.paymentsApi.processCartDeposit(this.booking.cookieId ?? '', card, this.accessToken);
      }

      const handleError = (e: { error: { message: string } }) => {
        this.busyService.set('pay', false);
        this.paying = false;
        if (this.booking) {
          this.booking.idempotencyKey = undefined;
        }
        this.braintreeError = e.error.message.split('\n').length > 1
          ? e.error.message.split('\n')[0]
          : e.error.message;
        this.dropinInstance?.clearSelectedPaymentMethod();
      };

      if (this.redirectToAdminAfterPayment) {
        processDeposit$
          .pipe(
            concatMap(confirmation => this.invoiceApi.getInvoice(confirmation, this.accessToken))
          )
          .subscribe(invoice => {
            this.authService.logout();

            if (this.booking?.cookieId) {
              this.bookingApi.deleteCart(this.booking.cookieId);
            }

            if (invoice.isSpecialEvent) {
              document.location.href = `${this.uaAdmin}/parties/special-events/${invoice.bookingId}`;
            }
            else {
              document.location.href = `${this.uaAdmin}/parties/booking/${invoice.bookingId}?parkId=${invoice.park.id}`;
            }
          }, handleError);
      } else {
        processDeposit$
          .subscribe(
            r => {
              this.sessionService.setBookingCookie(r);
              this.sessionService.removeCart();
              if (this.booking?.cookieId) {
                this.bookingApi.deleteCart(this.booking.cookieId);
              }
              this.busyService.set('pay', false);
              this.paying = false;
              this.sessionService.navigateToStep(NavigationSteps.Confirmation);
            },
            handleError);
      }
    });
  }

  setDropin(dropin: Dropin): void {
    this.dropinInstance = dropin;
    this.paymentInitialized = true;
  }

  setJustifiInitialized(elem: ElementRef): void {
    if (!elem || !elem.nativeElement || !elem.nativeElement.querySelector('justifi-card-form')) {
      const errMsg = 'Failed to load JustiFi component. Try refreshing the page.';
      this.handleError(errMsg);
      throw new Error(errMsg);  // get out of here
    }

    this.justifiElem = elem;
    this.justifiElem.nativeElement.setSelectedPaymentMethod({ type: 'new_card' });  // PAYMENT_METHODS.NEW_CARD

    // will go into this hook after filling out the payment form and submitting it to JustiFi
    this.justifiElem.nativeElement.preCompleteHook = (state: any, resolve: any, reject: any) => {
      const paymentToken: string = state?.paymentToken;

      if (!paymentToken) {
        this.handleError('Error in JustiFi: no paymentToken.');
        reject();
        return;
      }

      // Stop the JustiFi submission and handle payment ourselves
      reject();

      // Run inside Angular's zone — this callback is invoked by JustiFi's Stencil web component
      // outside Angular's zone, so state changes (BusyService, component properties) won't trigger
      // change detection unless we re-enter the zone explicitly.
      this.ngZone.run(() => {
        // Block re-entry into the payment processing below. Use walletProcessing instead
        // of `paying` because the card-form path already has paying=true here (set by
        // payNow), and gating on `paying` would abort the legitimate first entry.
        if (this.walletProcessing) {
          return;
        }
        this.walletProcessing = true;
        // Wallet payments (Apple/Google Pay) bypass payNow(); set paying here so the
        // [disabled] state still reflects in-flight processing. No-op for card-form.
        this.paying = true;

        // Wallet payments bypass payJustifi() too, so set the busy spinner here.
        this.justifiError = false;
        this.busyService.set('pay', true);

        this.checkPrepaidAndComplete(paymentToken);
      });
    };

    // Handle errors
    this.justifiElem.nativeElement.addEventListener('error-event', (event: any) => {
      console.error('Checkout error:', event.detail);

      const errMsg = event.detail?.message ?
        event.detail?.message?.toString()
        : 'Error in JustiFi checkout. Try refreshing the page.';

      this.handleError(errMsg);
    });

    // Handle checkout completion (this will never be reached since preCompleteHook will reject.
    /*
    this.justifiElem.nativeElement.addEventListener('submit-event', (event: any) => {
      console.log('Checkout completed successfully!', event.detail);

      // Payment method token: response.token. Full payment method data: response.data
      const response = event.detail.response;
      console.log('Tokenization successful:', response);

      this.payJustifiHandler(response.token);
    });
    */

    this.paymentInitialized = true; // set it at the end
  }

  get acknowledged(): boolean {
    return (this.documentsAcknowledged || (this.user?.impersonated ?? false)) && this.depositAcknowledged && this.weatherAcknowledged;
  }

  // [STORE-507] Staff Take Deposit (impersonated) returns to the admin site after payment. A vendor payment link that
  // carries `showConfirmation=true` is also impersonated but is paid by the customer, so it shows the
  // confirmation page instead.
  get redirectToAdminAfterPayment(): boolean {
    return !!this.user?.impersonated && !this.user?.showConfirmation;
  }

  get googlePayMerchantName(): string {
    const brand = this.brandName?.trim();
    const location = this.park?.name?.trim();

    if (brand && location) {
      return `${brand} ${location}`;
    }

    return brand || 'Amount';
  }

  getCheckoutTokenRequest(): CheckoutTokenRequest {
    return {
      amountInCents: Math.round((this.booking?.depositAmount ?? 0) * 100),  // must be >= 50 cents (Let JustiFi to do the validation?)
      // what should be used for description (it's a required field)??
      description: `Party booking deposit for ${this.user?.emailAddress} (accountId ${this.user?.accountId})`,
      originUrl: window.location.origin,
      currency: this.currencyCode,
      cookieId: this.booking?.cookieId,
    };
  }

  async addPaymentMethodClick(): Promise<void> {
    if (this.paymentProcessorVersion === 2) {
      this.justifiAuthToken = '';  // reset
      this.justifiCheckoutId = '';

      const brandSlug = this.brandOptions.brandSlug;
      const locationSlug = this.park?.urlSlug ?? '';
      const checkoutTokenRequest = this.getCheckoutTokenRequest();

      // Need authToken & checkoutId for JustiFi form initialization
      // The backend should request token using the platformSubAccountId (not the location one)
      let checkoutToken: CheckoutToken;
      try {
        checkoutToken = await this.justifiApiService.getCheckoutToken(brandSlug, locationSlug, checkoutTokenRequest)
          .pipe(take(1)).toPromise();
      } catch (err: any) {
        this.handleError(err?.error ?? err?.message ?? 'Failed to initialize payment form');
        return;
      }

      if (!checkoutToken?.checkoutId || !checkoutToken?.componentToken) {
        this.handleError('Error in getting JustiFi checkout token. Try refreshing the page.');
        return;
      }

      this.justifiAuthToken = checkoutToken.componentToken;
      this.justifiCheckoutId = checkoutToken.checkoutId;
    }

    // do this at the end (after the above async call has completed)
    this.addNewCard = true;
    this.useDefaultCard = false;
  }

  onDefaultChanged(): void {
    this.addNewCard = false;
    if (this.useDefaultCard){
      this.defaultCardValue = this.defaultCard?.id;
    }
  }

  // [CORE-485] label wallet-saved cards so the DPAN last-4 isn't mistaken for an unknown card
  walletPrefix(card?: PaymentMethod): string {
    return digitalWalletPrefix(card?.digitalWalletId);
  }

  stepChange(step: number): void {
    if (this.hideSteps || this.isSpecialEvent) {
      return;
    }

    switch (step) {
      case 1:
        this.sessionService.navigateToStep(NavigationSteps.Start);
        break;
      case 2:
        this.sessionService.navigateToStep(NavigationSteps.Review);
        break;
      case 3:
        break;
    }
  }
}
